if (!customElements.get('collection-tabs')) {
  // Eigener Slider: Theme-<carousel-slider> initialisiert versteckte Panels (0-Größe) nicht
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('.sct-tab');
      this.panels = this.querySelectorAll('.sct-panel');
      this.tabsContainer = this.querySelector('.sct-tabs');
      this._initialised = false;
    }

    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.bindTabEvents();
      this.panels.forEach((panel) => this.initPanel(panel));

      // nach erstem Paint messen (Card-Breiten existieren erst dann)
      requestAnimationFrame(() => this.refreshActivePanel());

      this._onResize = this._debounce(() => {
        this.panels.forEach((panel) => this.updateButtonStates(panel));
      }, 150);
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    bindTabEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.switchTab(tab));

        // Prefetch bei Hover/Fokus: Inhalt ist beim Klick i. d. R. schon da
        const prefetch = () => {
          const panel = this.panelForTab(tab);
          if (panel && panel.dataset.loaded === 'false' && !panel._sctLoading) {
            this.loadPanel(panel);
          }
        };
        tab.addEventListener('pointerenter', prefetch);
        tab.addEventListener('focus', prefetch);
      });
    }

    panelForTab(tab) {
      const id = tab.getAttribute('aria-controls');
      return Array.from(this.panels).find((p) => p.id === id) || null;
    }

    initPanel(panel) {
      if (panel._sctInited) return;

      const slider = panel.querySelector('.sct-slider');
      const nav = panel.querySelector('.sct-slider-nav');
      if (!slider || !nav) return;

      const prevBtn = nav.querySelector('button[name="prev"]');
      const nextBtn = nav.querySelector('button[name="next"]');
      if (!prevBtn || !nextBtn) return;

      panel._sctInited = true;
      panel._sctSlider = slider;
      panel._sctNav = nav;
      panel._sctPrevBtn = prevBtn;
      panel._sctNextBtn = nextBtn;

      prevBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.scrollPanel(panel, -1);
      });
      nextBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.scrollPanel(panel, 1);
      });

      slider.addEventListener(
        'scroll',
        this._debounce(() => this.updateButtonStates(panel), 60),
        { passive: true }
      );
    }

    scrollPanel(panel, direction) {
      const slider = panel._sctSlider;
      if (!slider) return;

      const items = slider.querySelectorAll('.slider__item');
      if (items.length < 2) return;

      const firstRect = items[0].getBoundingClientRect();
      const secondRect = items[1].getBoundingClientRect();
      const itemSpan = Math.abs(secondRect.left - firstRect.left);
      if (!itemSpan) {
        slider.scrollBy({ left: direction * slider.clientWidth * 0.9, behavior: 'smooth' });
        return;
      }

      slider.scrollBy({ left: direction * itemSpan, behavior: 'smooth' });
    }

    updateButtonStates(panel) {
      const slider = panel._sctSlider;
      const nav = panel._sctNav;
      const prevBtn = panel._sctPrevBtn;
      const nextBtn = panel._sctNextBtn;
      if (!slider || !nav || !prevBtn || !nextBtn) return;

      // versteckt (display:none) → Maße 0; später bei aktivem Tab neu prüfen
      if (slider.clientWidth === 0) {
        nav.dataset.hidden = 'true';
        return;
      }

      const hasOverflow = slider.scrollWidth - slider.clientWidth > 1;
      nav.dataset.hidden = hasOverflow ? 'false' : 'true';

      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const left = slider.scrollLeft;
      prevBtn.disabled = left <= 1;
      nextBtn.disabled = left >= maxScroll - 1;
    }

    refreshActivePanel() {
      const activePanel = Array.from(this.panels).find((p) => p.classList.contains('is-active'));
      if (activePanel) this.updateButtonStates(activePanel);
    }

    switchTab(selectedTab) {
      const targetId = selectedTab.getAttribute('aria-controls');

      this.tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', tab === selectedTab ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const isTarget = panel.id === targetId;
        panel.classList.toggle('is-active', isTarget);
        if (isTarget) this.activatePanel(panel);
      });

      this.ensureTabVisible(selectedTab);
    }

    activatePanel(panel) {
      if (panel.dataset.loaded === 'true') {
        // nach display:block braucht es einen Frame für korrekte Maße
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initPanel(panel);
            this.updateButtonStates(panel);
          });
        });
        return;
      }
      this.loadPanel(panel);
    }

    async loadPanel(panel) {
      if (panel._sctLoading || panel.dataset.loaded === 'true') return;

      const url = panel.dataset.url;
      if (!url) {
        this.renderEmpty(panel);
        return;
      }

      panel._sctLoading = true;
      panel.classList.add('is-loading');

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const inner = tmpl.content.querySelector('[data-sct-panel-inner]');
        if (!inner) throw new Error('no panel content');

        const existing = panel.querySelector('[data-sct-panel-inner]');
        if (existing) existing.replaceWith(inner);
        else panel.appendChild(inner);

        panel.dataset.loaded = 'true';
        panel._sctInited = false;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initPanel(panel);
            this.updateButtonStates(panel);
          });
        });
      } catch (error) {
        this.renderEmpty(panel);
      } finally {
        panel._sctLoading = false;
        panel.classList.remove('is-loading');
      }
    }

    renderEmpty(panel) {
      const msg = this.dataset.emptyText || 'Es konnten keine Produkte gefunden werden.';
      const html = '<div class="sct-panel__inner" data-sct-panel-inner><div class="container"><p class="sct-empty">' + msg + '</p></div></div>';
      const existing = panel.querySelector('[data-sct-panel-inner]');
      if (existing) existing.outerHTML = html;
      else panel.insertAdjacentHTML('beforeend', html);
      panel.dataset.loaded = 'true';
    }

    ensureTabVisible(tab) {
      if (!this.tabsContainer || !tab) return;
      const containerRect = this.tabsContainer.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();

      if (tabRect.left < containerRect.left) {
        this.tabsContainer.scrollBy({
          left: tabRect.left - containerRect.left - 16,
          behavior: 'smooth',
        });
      } else if (tabRect.right > containerRect.right) {
        this.tabsContainer.scrollBy({
          left: tabRect.right - containerRect.right + 16,
          behavior: 'smooth',
        });
      }
    }

    _debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}
